import { getAyahKey, getAyahNumber, getSurahNumber, getTranslationForAyah } from '@/lib/mushafAyah'

export const BOOKMARKS_STORAGE_KEY = 'quranBookmarkedAyahs'
export const BOOKMARKS_UPDATED_AT_KEY = 'quranBookmarksUpdatedAt'
/** Fired on the window whenever bookmarks change locally (drives cloud sync). */
export const BOOKMARKS_CHANGED_EVENT = 'quran:bookmarks-changed'
/** Fired after a cloud pull replaces local bookmarks (views should reload). */
export const BOOKMARKS_SYNCED_EVENT = 'quran:bookmarks-synced'

export type AyahBookmark = {
  key: string
  surah: number
  ayah: number
  arabicText: string
  translation: string
  page: number
  savedAt: string
}

export function loadAyahBookmarks(): AyahBookmark[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(BOOKMARKS_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as AyahBookmark[]
    if (!Array.isArray(parsed)) return []
    return parsed
  } catch {
    return []
  }
}

export function getBookmarksUpdatedAt(): string | null {
  if (typeof window === 'undefined') return null
  return window.localStorage.getItem(BOOKMARKS_UPDATED_AT_KEY)
}

/**
 * Persist bookmarks locally. `silent` writes (used when applying a freshly pulled remote
 * copy) skip the change event so they don't trigger a redundant push back to the server.
 */
export function saveAyahBookmarks(bookmarks: AyahBookmark[], options?: { updatedAt?: string; silent?: boolean }) {
  if (typeof window === 'undefined') return
  const updatedAt = options?.updatedAt || new Date().toISOString()
  window.localStorage.setItem(BOOKMARKS_STORAGE_KEY, JSON.stringify(bookmarks))
  window.localStorage.setItem(BOOKMARKS_UPDATED_AT_KEY, updatedAt)
  if (!options?.silent) {
    window.dispatchEvent(new CustomEvent(BOOKMARKS_CHANGED_EVENT))
  }
}

export function toggleAyahBookmark(ayah: any, page: number, translationPageData: unknown): AyahBookmark[] {
  const key = getAyahKey(ayah)
  const existing = loadAyahBookmarks()
  const index = existing.findIndex((item) => item.key === key)
  if (index >= 0) {
    existing.splice(index, 1)
    saveAyahBookmarks(existing)
    return existing
  }
  const next: AyahBookmark = {
    key,
    surah: getSurahNumber(ayah),
    ayah: getAyahNumber(ayah),
    arabicText: String(ayah?.text || ''),
    translation: getTranslationForAyah(ayah, translationPageData),
    page,
    savedAt: new Date().toISOString(),
  }
  existing.unshift(next)
  saveAyahBookmarks(existing)
  return existing
}
